/**
 * Jubelio Shipment Tutorial — Iterasi 2, Rev Feb 2026
 *
 * Struktur hybrid:
 *   1. Guide Page  (`/admin/tutorials/jubelio`)  — panduan bergambar 9 langkah
 *      (login Jubelio → API Key → Hubungkan ke Dynicty → Tes Koneksi →
 *      Atur Pengiriman → Atur Gudang → Buat Resi → Batalkan → Buat Resi Ulang).
 *   2. In-App Walkthrough — spotlight overlay yang memandu user MENGISI
 *      Pengaturan Jubelio + Shipping di halaman Pengaturan Dynicty.
 *
 * Kontrak step overlay (compat dengan Basic tutorial):
 *   id, route, target, featureKey, title, body, informational, placement
 *
 * Progress hanya tercatat untuk in-app walkthrough. Guide page adalah
 * halaman edukasi statis (tidak menambah/mengurangi stepIndex).
 *
 * SEMUA TEKS wajib Bahasa Indonesia sederhana, tanpa istilah teknis.
 */

// ─── Guide-page reading sections ───────────────────────────────────────────
// 9 langkah utama, tiap langkah punya 1–2 screenshot (subImage).
// image = screenshot utama, subImage = screenshot lanjutan (opsional).
export const JUBELIO_GUIDE_SECTIONS = [
  {
    id: "login",
    number: 1,
    title: "Masuk ke Akun Jubelio",
    body:
      "Buka Jubelio, kemudian masuk menggunakan akun Jubelio Anda.",
    image: "/tutorials/jubelio/jubelio-step-01A.jpg",
    imageAlt: "Halaman masuk Jubelio",
    subImage: "/tutorials/jubelio/jubelio-step-01B.jpg",
    subImageAlt: "Halaman utama Jubelio setelah berhasil masuk",
    subBody:
      "Setelah berhasil masuk, Anda akan berada di halaman utama Jubelio.",
  },
  {
    id: "apikey",
    number: 2,
    title: "Dapatkan Kunci Jubelio (API Key)",
    body:
      "Di Jubelio, buka menu Settings kemudian pilih Developer.",
    image: "/tutorials/jubelio/jubelio-step-02A.jpg",
    imageAlt: "Menu Developer di halaman Settings Jubelio",
    subImage: "/tutorials/jubelio/jubelio-step-02B.jpg",
    subImageAlt: "Bagian API Key pada halaman Developer",
    subBody:
      "Pada halaman Developer, cari bagian API Key. Informasi pada bagian ini akan digunakan untuk menghubungkan Jubelio dengan Dynicty.",
    note:
      "Simpan informasi ini dengan aman dan jangan membagikannya kepada pihak yang tidak berkepentingan.",
  },
  {
    id: "hubungkan",
    number: 3,
    title: "Hubungkan Jubelio ke Dynicty",
    body:
      "Selanjutnya buka admin Dynicty, lalu masuk ke Pengaturan → Jubelio. Di halaman ini Anda akan menghubungkan akun Jubelio dengan toko Anda.",
    image: "/tutorials/jubelio/jubelio-step-03A.jpg",
    imageAlt: "Halaman Pengaturan Jubelio di Dynicty sebelum diisi",
    subImage: "/tutorials/jubelio/jubelio-step-03B.jpg",
    subImageAlt: "Halaman Pengaturan Jubelio setelah informasi diisi",
    subBody:
      "Masukkan informasi Jubelio ke kolom yang tersedia: Client ID, Client Secret, Nama Pengirim, Nomor HP, Alamat, Kota, dan Kode Pos.",
    note:
      "Pastikan Client Secret tidak dibagikan kepada pihak lain.",
  },
  {
    id: "test",
    number: 4,
    title: "Tes Koneksi Jubelio",
    body:
      "Setelah semua informasi terisi, klik Simpan Pengaturan terlebih dahulu, lalu klik Tes Koneksi. Dynicty akan memverifikasi Client ID dan Client Secret yang tersimpan langsung ke server Jubelio. Warehouse ID tidak diperlukan untuk tes ini.",
    image: "/tutorials/jubelio/jubelio-step-04A.jpg",
    imageAlt: "Halaman Pengaturan Jubelio dalam kondisi terhubung",
  },
  {
    id: "shipping",
    number: 5,
    title: "Atur Pengiriman",
    body:
      "Buka Pengaturan Pengiriman di Dynicty, lalu pilih Jubelio sebagai layanan pengiriman utama.",
    image: "/tutorials/jubelio/jubelio-step-05A.jpg",
    imageAlt: "Pengaturan Pengiriman dengan Jubelio dipilih sebagai layanan utama",
    subTitle: "Atur Alamat Gudang",
    subImage: "/tutorials/jubelio/jubelio-step-05B.jpg",
    subImageAlt: "Pengaturan alamat gudang / alamat asal pengiriman",
    subBody:
      "Selanjutnya tentukan alamat gudang atau alamat asal pengiriman. Alamat ini digunakan sebagai titik awal pengiriman dan membantu sistem menghitung ongkir dengan benar.",
  },
  {
    id: "buatresi",
    number: 6,
    title: "Buat Resi Pesanan",
    body:
      "Setelah ada pesanan yang menggunakan pengiriman Jubelio, buka detail pesanan lalu klik Buat Resi untuk membuat nomor resi pengiriman.",
    image: "/tutorials/jubelio/jubelio-step-06A.jpg",
    imageAlt: "Detail pesanan dengan tombol Buat Resi",
    subImage: "/tutorials/jubelio/jubelio-step-06B.jpg",
    subImageAlt: "Konfirmasi Buat Resi Jubelio",
    subBody:
      "Dynicty akan menampilkan konfirmasi sebelum resi dibuat. Periksa pesanan, kemudian klik Buat Resi untuk melanjutkan.",
  },
  {
    id: "resisukses",
    number: 7,
    title: "Resi Berhasil Dibuat",
    body:
      "Setelah berhasil, nomor resi akan muncul pada detail pesanan. Informasi kurir dan status pengiriman juga dapat dilihat dari halaman ini.",
    image: "/tutorials/jubelio/jubelio-step-07A.jpg",
    imageAlt: "Detail pesanan menampilkan nomor resi, kurir, dan status pengiriman",
  },
  {
    id: "batalkan",
    number: 8,
    title: "Batalkan Pengiriman",
    body:
      "Jika pengiriman perlu dibatalkan, klik Batalkan Pengiriman. Masukkan alasan pembatalan jika diminta, kemudian konfirmasi pembatalan.",
    image: "/tutorials/jubelio/jubelio-step-08A.jpg",
    imageAlt: "Modal Batalkan Pengiriman dengan kolom alasan pembatalan",
    subImage: "/tutorials/jubelio/jubelio-step-08B.jpg",
    subImageAlt: "Detail pesanan setelah pengiriman berhasil dibatalkan",
    subBody:
      "Setelah pembatalan berhasil, status pengiriman akan berubah menjadi dibatalkan. Jika diperlukan, Anda dapat membuat resi baru dengan memilih Buat Resi Ulang.",
  },
  {
    id: "resiulang",
    number: 9,
    title: "Buat Resi Ulang",
    body:
      "Klik Buat Resi Ulang jika Anda ingin membuat resi baru untuk pengiriman yang sebelumnya sudah dibatalkan. Resi sebelumnya tetap tersimpan dalam riwayat pengiriman. Klik Buat Resi Baru untuk melanjutkan.",
    image: "/tutorials/jubelio/jubelio-step-09A.jpg",
    imageAlt: "Modal Buat Resi Ulang dengan tombol Buat Resi Baru",
  },
];

// ─── In-app spotlight walkthrough steps ────────────────────────────────────
// Overlay memandu user mengisi Pengaturan Jubelio + Shipping. Tanpa langkah
// khusus untuk Warehouse ID (informasi Jubelio kini hanya Client ID + Secret).
export const JUBELIO_TUTORIAL = {
  id: "jubelio-shipment",
  title: "Jubelio Shipment",
  subtitle:
    "Panduan lengkap menghubungkan Jubelio: ongkir, kurir, resi, dan tracking.",
  estimatedMinutes: 6,
  featureKey: "integration.jubelio",
  guidePagePath: "/admin/tutorials/jubelio",
  steps: [
    {
      id: "start",
      informational: true,
      title: "Ayo Mulai",
      body:
        "Kita akan buka Pengaturan Jubelio di Dynicty. Ikuti panduan dan isi kolom sesuai data yang sudah Anda salin dari akun Jubelio.",
    },
    {
      id: "enable",
      route: "/admin/jubelio",
      target: '[data-tour="jubelio-enable"]',
      title: "Aktifkan Jubelio",
      body:
        "Nyalakan tombol ini untuk mulai memakai Jubelio. Kalau dimatikan, toko tetap bisa jalan tapi tanpa layanan Jubelio.",
    },
    {
      id: "client-id",
      route: "/admin/jubelio",
      target: '[data-tour="jubelio-client-id"]',
      title: "Client ID",
      body:
        "Tempel Client ID yang tadi Anda salin dari akun Jubelio ke kolom ini. Client ID adalah nama pengenal toko Anda di sistem Jubelio.",
    },
    {
      id: "client-secret",
      route: "/admin/jubelio",
      target: '[data-tour="jubelio-client-secret"]',
      title: "Client Secret",
      body:
        "Tempel Client Secret yang tadi Anda salin. Data ini adalah kunci rahasia. Jangan pernah dibagikan ke orang lain.",
    },
    {
      id: "sender",
      route: "/admin/jubelio",
      target: '[data-tour="jubelio-sender"]',
      title: "Data Pengirim",
      body:
        "Isi nama, nomor HP, dan alamat pengirim. Data ini yang akan dicetak di label pengiriman. Contoh nama: Toko Roti Bu Ani.",
    },
    {
      id: "save",
      route: "/admin/jubelio",
      target: '[data-tour="jubelio-save"]',
      title: "Simpan Pengaturan",
      body:
        "Setelah semua kolom terisi, klik Simpan Pengaturan. Kalau tersimpan, akan muncul pesan berhasil.",
    },
    {
      id: "test-connection",
      route: "/admin/jubelio",
      target: '[data-tour="jubelio-test"]',
      title: "Tes Koneksi",
      body:
        "Setelah pengaturan tersimpan, klik Tes Koneksi untuk memverifikasi Client ID dan Client Secret langsung ke server Jubelio. Kalau berhasil, akan muncul tanda koneksi berhasil.",
    },
    {
      id: "shipping-provider",
      route: "/admin/shipping",
      target: '[data-tour="jubelio-provider-radio"]',
      title: "Pilih Jubelio sebagai Layanan Pengiriman",
      body:
        "Di halaman Pengiriman, pilih Jubelio sebagai layanan pengiriman utama. Setelah itu pelanggan akan lihat pilihan kurir Jubelio saat checkout.",
    },
    {
      id: "shipping-warehouse",
      route: "/admin/shipping",
      target: '[data-tour="shipping-warehouse"]',
      title: "Atur Alamat Gudang",
      body:
        "Isi alamat gudang lengkap (Provinsi → Kota → Kecamatan → Kelurahan). Alamat ini dipakai sebagai titik awal pengiriman.",
    },
    {
      id: "shipping-save",
      route: "/admin/shipping",
      target: '[data-tour="shipping-save"]',
      title: "Simpan Pengaturan Pengiriman",
      body:
        "Klik Simpan Pengaturan Pengiriman. Setelah tersimpan, pelanggan bisa mulai melihat ongkir Jubelio di checkout.",
    },
    {
      id: "orders-page",
      route: "/admin/pesanan",
      target: '[data-tour="orders-page"]',
      title: "Halaman Pesanan",
      body:
        "Semua pesanan yang masuk muncul di sini. Klik satu pesanan untuk lihat detail dan membuat resi.",
    },
    {
      id: "create-resi-info",
      informational: true,
      title: "Buat Resi",
      body:
        "Di dalam detail pesanan, ada tombol Buat Resi. Klik tombol itu setelah pesanan siap dikirim, dan Jubelio akan memberi nomor resi otomatis.",
    },
    {
      id: "tracking-info",
      informational: true,
      title: "Lihat Status Pengiriman",
      body:
        "Setelah resi dibuat, panel Tracking Pengiriman akan muncul di detail pesanan. Anda bisa lihat perjalanan paket dari Jubelio kapan saja.",
    },
    {
      id: "cancel-info",
      informational: true,
      title: "Batalkan Pengiriman",
      body:
        "Kalau pengiriman perlu dibatalkan, klik Batalkan Pengiriman. Isi alasan pembatalan minimal 3 karakter.",
    },
    {
      id: "regenerate-info",
      informational: true,
      title: "Buat Resi Ulang",
      body:
        "Kalau pengiriman sebelumnya sudah dibatalkan tapi Anda tetap ingin mengirim pesanannya, klik Buat Resi Ulang. Resi lama tetap tersimpan di riwayat pengiriman.",
    },
    {
      id: "done",
      informational: true,
      title: "Selesai",
      body:
        "Panduan Jubelio selesai. Anda bisa mengulangnya kapan saja dari Pusat Panduan.",
    },
  ],
};
